if (!customElements.get("kw-cart-guard")) {
  const STORAGE_KEY = "kw-cart-guard-removed";
  const NOTICE_TTL = 10 * 60 * 1000;
  const DEFAULT_TEXT = "[titel] ist nicht mehr bestellbar und wurde aus dem Warenkorb entfernt.";
  const MAX_RUNS = 4;
  const RUN_WINDOW = 20000;
  const ADDED_BY = /^Added by:\s*(\S+)/;
  const runs = [];
  let running = null;

  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;

  const apoOrder = (item) => {
    const value = item.properties && item.properties._apo_order;
    return typeof value === "string" ? value.trim() : "";
  };
  const componentOf = (item) => {
    const match = apoOrder(item).match(ADDED_BY);
    return match ? match[1] : null;
  };
  const parentOf = (item) => {
    const value = apoOrder(item);
    return value && !ADDED_BY.test(value) ? value : null;
  };

  const getCart = () =>
    fetch(route("cart_url", "/cart") + ".js", { headers: { Accept: "application/json" } }).then((response) =>
      response.json()
    );

  const readNotice = () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      return stored && Date.now() - stored.time < NOTICE_TTL ? stored : null;
    } catch (error) {
      return null;
    }
  };
  const writeNotice = (titles) => {
    const stored = readNotice();
    const all = [...new Set([...(stored ? stored.titles : []), ...titles])];
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ time: Date.now(), titles: all }));
    } catch (error) {}
  };
  const clearNotice = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {}
  };

  function collect(cart, productIds, removeSets) {
    const keys = new Set();
    const titles = new Set();
    const addSet = (setId) =>
      cart.items.forEach((line) => {
        if (parentOf(line) === setId || componentOf(line) === setId) keys.add(line.key);
      });

    cart.items.forEach((item) => {
      if (!productIds.has(item.product_id)) return;

      const setId = componentOf(item);
      if (setId) {
        if (!removeSets) return;
        const parent = cart.items.find((line) => parentOf(line) === setId);
        addSet(setId);
        keys.add(item.key);
        titles.add(parent ? `${parent.product_title} (enthält: ${item.product_title})` : item.product_title);
        return;
      }

      const ownSet = parentOf(item);
      if (ownSet) addSet(ownSet);
      keys.add(item.key);
      titles.add(item.product_title);
    });

    cart.items.forEach((line) => {
      const relation = line.parent_relationship;
      if (relation && keys.has(relation.parent_key)) keys.add(line.key);
    });

    return { keys: [...keys], titles: [...titles] };
  }

  async function removeArchived(productIds, removeSets) {
    const cart = await getCart();
    const { keys, titles } = collect(cart, productIds, removeSets);
    if (!keys.length) return { cart, changed: false };

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    const response = await fetch(route("cart_update_url", "/cart/update") + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error("cart update failed: " + response.status);

    writeNotice(titles);
    return { cart: await response.json(), changed: true };
  }

  async function refresh(cart, mayReload) {
    const hosts = [...document.querySelectorAll("cart-items, mini-cart")];
    try {
      await Promise.all(
        hosts.map((host) => (typeof host.updateSectionContents === "function" ? host.updateSectionContents() : null))
      );
    } catch (error) {
      if (mayReload && document.querySelector("cart-items")) window.location.reload();
      return;
    }

    const empty = cart.item_count === 0;
    document.querySelectorAll("cart-items").forEach((host) => host.classList.toggle("is-empty", empty));
    const footer = document.getElementById("main-cart-footer");
    if (footer) footer.classList.toggle("is-empty", empty);

    document.dispatchEvent(new CustomEvent("cart:item-remove", { detail: { cart, context: "CartGuard" } }));
  }

  function run(guard) {
    if (running) return running;

    const now = Date.now();
    while (runs.length && now - runs[0] > RUN_WINDOW) runs.shift();
    if (runs.length >= MAX_RUNS) return Promise.resolve();
    runs.push(now);

    running = removeArchived(guard.productIds, guard.dataset.components === "remove_set")
      .then(({ cart, changed }) => refresh(cart, changed))
      .catch(() => {})
      .finally(() => {
        running = null;
      });
    return running;
  }

  const isLocked = () => !!document.querySelector("kw-cart-guard[data-locked]");

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest && event.target.closest('[name="checkout"]');
      if (!button || !isLocked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
  document.addEventListener(
    "submit",
    (event) => {
      if (!event.submitter || event.submitter.name !== "checkout" || !isLocked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  customElements.define(
    "kw-cart-guard",
    class KwCartGuard extends HTMLElement {
      connectedCallback() {
        this.showNotice();
        if (this.productIds.size) run(this);
      }

      get productIds() {
        try {
          return new Set(JSON.parse(this.dataset.products || "[]"));
        } catch (error) {
          return new Set();
        }
      }

      showNotice() {
        const box = this.querySelector("[data-notice]");
        if (!box) return;

        const stored = readNotice();
        box.textContent = "";
        box.hidden = !stored || !stored.titles.length;
        if (box.hidden) return;

        const template = this.dataset.text || DEFAULT_TEXT;
        stored.titles.forEach((title) => {
          const line = document.createElement("p");
          line.textContent = template.includes("[titel]") ? template.replace("[titel]", title) : `${title}: ${template}`;
          box.append(line);
        });

        const close = document.createElement("button");
        close.type = "button";
        close.className = "kw-cart-guard__close";
        close.setAttribute("aria-label", "Hinweis schließen");
        close.textContent = "×";
        close.addEventListener("click", () => {
          clearNotice();
          document.querySelectorAll("kw-cart-guard").forEach((guard) => guard.showNotice());
        });
        box.append(close);
      }
    }
  );
}
